import { filter } from 'lodash';
import { useEffect, useState, useContext } from 'react';
import { useSearchParams } from 'react-router-dom';
// material
import {
  Card,
  Table,
  Stack,
  TableRow,
  TableBody,
  TableCell,
  Container,
  Typography,
  TableContainer,
  TablePagination,
  Chip,
  Alert,
  Box,
  CircularProgress
} from '@mui/material';
// contexts
import { useAuth } from '../../contexts/Auth';
import { SettingsContext } from '../../contexts/Settings';
// helpers
import { getTimeZoneDate, getTimeZoneGTM } from '../../helpers/Util';
// services
import { getEntryList } from '../../services/Report';
// components
import Page from '../../components/Page';
import Scrollbar from '../../components/Scrollbar';
import SearchNotFound from '../../components/SearchNotFound';
import { EntryListHead, EntryListToolbar } from '../../components/_dashboard/entry';

// ----------------------------------------------------------------------

const TABLE_HEAD = [
  { id: 'number', label: '# Order Number', alignRight: false, width: 200 },
  { id: 'date', label: 'Entry time at', alignRight: false, width: 250 },
  { id: 'gate', label: 'Gate', alignRight: false, width: 150 },
  { id: 'section', label: 'Section', alignRight: false, with: 200 },
  { id: 'tickets', label: 'Tickets', alignRight: false },
  { id: 'status', label: 'Status', alignRight: false, width: 170 }
];

// ----------------------------------------------------------------------

function descendingComparator(a, b) {
  if (b.createdAt < a.createdAt) {
    return -1;
  }
  if (b.createdAt > a.createdAt) {
    return 1;
  }
  return 0;
}

function getComparator(sort) {
  return sort === 'desc'
    ? (a, b) => descendingComparator(a, b)
    : (a, b) => -descendingComparator(a, b);
}

function applySortFilter(array, comparator, query) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const sort = comparator(a[0], b[0]);
    if (sort !== 0) return sort;
    return a[1] - b[1];
  });

  if (query) {
    return filter(
      array,
      (_event) => _event.order.number.toLowerCase().indexOf(query.toLowerCase()) !== -1
    );
  }
  return stabilizedThis.map((el) => el[0]);
}

export default function EntryList() {
  const [searchParams] = useSearchParams();

  const [entries, setEntries] = useState([]);
  const [page, setPage] = useState(0);
  const [sort, setSort] = useState('desc');
  const [sortBy, setSortBy] = useState('date');
  const [filterName, setFilterName] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  const { authUser } = useAuth();
  const { authSettings } = useContext(SettingsContext);

  const handleRequestSort = (event, property) => {
    const isAsc = sortBy === property && sort === 'asc';
    setSort(isAsc ? 'desc' : 'asc');
    setSortBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterByName = (event) => {
    setFilterName(event.target.value);
  };

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - entries.length) : 0;

  useEffect(() => {
    const fetchData = async (eventId) => {
      setIsLoading(true);

      try {
        let eventDate = null;
        let start = null;
        let end = null;

        const queryEventDate = searchParams.get('eventDate');
        const queryDateStart = searchParams.get('start');
        const queryDateEnd = searchParams.get('end');

        if (queryEventDate) {
          eventDate = getTimeZoneDate({ date: queryEventDate }).toISOString();
        }
        if (queryDateStart) {
          start = getTimeZoneDate({ date: queryDateStart }).toISOString();
        }
        if (queryDateEnd) {
          end = getTimeZoneDate({ date: queryDateEnd }).toISOString();
        }

        const idToken = await authUser.getIdToken();
        const headers = {
          Authorization: `Bearer ${idToken}`,
          'Account-Key': authSettings.server.path
        };

        const entries = await getEntryList({ headers, eventId, eventDate, start, end });

        setEntries(entries);
        setIsLoading(false);
      } catch (error) {
        setHasError(true);
        console.error(error);
      }
    };

    const eventId = searchParams.get('eventId');
    if (eventId) {
      fetchData(eventId);
    }
  }, [searchParams, authUser, authSettings, setEntries]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'ENTERED':
        return 'success';
      case 'BLOCKED':
        return 'error';
      default:
        return '';
    }
  };

  const handleClickEntyRow = (entryId) => {
    window.location.href = `/entry/${entryId}?eventId=${searchParams.get('eventId')}`;
  };

  const filteredEntries = applySortFilter(entries, getComparator(sort), filterName);

  const isEntryNotFound = filteredEntries.length === 0;

  if (hasError) {
    return (
      <Container>
        <Alert severity="error">
          An error occurred while trying to load the entries — Please, try to refresh the page!
        </Alert>
      </Container>
    );
  }

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={3}>
        <CircularProgress color="inherit" />
      </Box>
    );
  }

  return (
    <Page title="Event | Voy">
      <Container>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Typography variant="h4">Entries</Typography>
        </Stack>

        <Card>
          <EntryListToolbar filterName={filterName} onFilterName={handleFilterByName} />

          <Scrollbar>
            <TableContainer sx={{ minWidth: 1060 }}>
              <Table>
                <EntryListHead
                  sort={sort}
                  sortBy={sortBy}
                  headLabel={TABLE_HEAD}
                  rowCount={entries.length}
                  onRequestSort={handleRequestSort}
                />
                <TableBody>
                  {filteredEntries
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row) => {
                      const { _id } = row;

                      return (
                        <TableRow
                          hover
                          key={_id}
                          tabIndex={-1}
                          onClick={() => handleClickEntyRow(row._id)}
                          sx={{ cursor: 'pointer' }}
                        >
                          <TableCell component="th" scope="row">
                            {row.order.number}
                          </TableCell>
                          <TableCell>
                            {`${getTimeZoneDate({ date: row.booking.entry.time })
                              .tz('Europe/Lisbon')
                              .format('ddd, DD MMM, HH:mm:ss')}
                              (GTM${getTimeZoneGTM({ date: row.booking.entry.time })})`}
                          </TableCell>
                          <TableCell>Gate 1</TableCell>
                          <TableCell>
                            {row.booking.section.single ? ' - ' : row.booking.section.name}
                          </TableCell>
                          <TableCell>{row.booking.ticket.name}</TableCell>
                          <TableCell>
                            <Chip
                              label={row.reasons.length === 0 ? 'ENTERED' : 'BLOCKED'}
                              color={getStatusColor(
                                row.reasons.length === 0 ? 'ENTERED' : 'BLOCKED'
                              )}
                              size="small"
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  {emptyRows > 0 && (
                    <TableRow style={{ height: 53 * emptyRows }}>
                      <TableCell colSpan={6} />
                    </TableRow>
                  )}
                </TableBody>
                {isEntryNotFound && (
                  <TableBody>
                    <TableRow>
                      <TableCell align="center" colSpan={7} sx={{ py: 3 }}>
                        <SearchNotFound searchQuery={filterName} />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Scrollbar>

          <TablePagination
            rowsPerPageOptions={[25, 100, 250]}
            component="div"
            count={entries.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Card>
      </Container>
    </Page>
  );
}
